'use client'

import { useEffect, useRef, useState } from 'react'
import { Trash2, X, Minus, Plus, MapPin, Store, Bike, Package, Check, ChevronRight, Tag, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { CartItem, DeliveryMode, SoldOutNotice } from '../types'
import { retailStockBadge, type RetailStockView } from '../useRetailStock'
import type { MicrositeCartMenuItem } from '../RestaurantMicrosite'
import { PhoneInput } from '@/components/ui'
import type { OpenStatus } from '@/lib/hours/openStatus'
import type { ScheduleDay } from '@/lib/hours/scheduleSlots'
import { AcceptedCards } from '../AcceptedCards'
import { ClosedNotice } from '../OpenStatusBadge'
import { SchedulePicker, type ScheduleMode } from '../SchedulePicker'
import { WhyFeeLink } from '../WhyFeeLink'
import { formatModifierLabel } from '@/lib/menu/modifierQuantity'
import { nameGateSatisfied, phoneGateSatisfied } from './qrTableFields'
import type { PricingConfig } from '@/lib/utils/pricing'
import type { CouponReasonKey } from '@/lib/kiosk/couponReasonMessage'
import { couponShortfall } from '@/lib/coupons/shortfall'
import {
  runCouponApply,
  micrositeCouponRequestLines,
  micrositeLineSubtotal,
  micrositeLineName,
  micrositeWeightLabel,
  deepLinkInitialInput,
  deepLinkDecision,
  type AppliedCoupon,
  type MicrositeLocale,
  type SessionCouponRejection,
} from '@/lib/microsite/couponUx'
import { VisitRewardWebOffer } from './VisitRewardWebOffer'
import type { AppliedVisitReward, VisitRewardNotice } from '@/lib/microsite/visitRewardUx'
import { cartLineSubstitutionCandidates } from '@/lib/microsite/substitutionCandidates'
import { waitText, withCourier, type WaitEstimate } from '@/lib/microsite/waitEstimate'
import { SubstitutionLineSheet } from './SubstitutionLineSheet'
import type { GateResult } from '@/lib/microsite/checkoutV2'
import { groupCartLines } from '@/lib/storefront/cartSections'
import type { StorefrontSection } from '@/lib/microsite/storefrontKind'

type Props = {
  cart: CartItem[]
  setCart: (cart: CartItem[]) => void
  /** Config de precios del local (redondeo de tarjeta para el DISPLAY de cada línea). */
  pricingCfg?: PricingConfig
  /** Idioma de la tienda: separador decimal del peso en el nombre de la línea (súper online). */
  locale?: MicrositeLocale
  subtotal: number
  taxAmount: number
  /** Precios con impuesto incluido: la fila se rotula "incluido" (no se suma). */
  taxIncluded?: boolean
  /** PHASE F: per-transaction processing fee charged at checkout. 0 when feature off. */
  processingFee?: number
  /**
   * Envío del courier en dólares. Viene SIEMPRE de `heldQuote.feeAmountCents / 100`
   * (OrderSheet) — jamás se recalcula acá. 0 en pickup o sin cotización.
   */
  deliveryFee?: number
  total: number
  accentColor: string
  /** false => bloquea avanzar a pago (local cerrado o en ventana de last-call). */
  acceptingOrders?: boolean
  openStatus?: OpenStatus | null
  /** Botón de pánico: ventas online pausadas. Bloquea el pago en AMBOS modos (ASAP y agendado). */
  orderingPaused?: boolean
  /** Instante ISO en que la pausa se auto-levanta, o null si es manual (informativo). */
  pauseResumesAt?: string | null
  /** Fase 2 — agendado de pedidos. */
  schedulingEnabled?: boolean
  scheduleDays?: ScheduleDay[]
  scheduleMode?: ScheduleMode
  scheduledFor?: string | null
  onChangeMode?: (mode: ScheduleMode) => void
  onChangeSlot?: (iso: string | null) => void
  /**
   * Modo mesa: campo de nombre, OBLIGATORIO (bloquea el pago si está vacío).
   *
   * Las tres van REQUERIDAS, al revés que las del teléfono de abajo: acá el
   * handler es parte del contrato. Con `showTableName` en true y sin
   * `onChangeTableName`, el input no se podría llenar nunca y `canCheckout`
   * quedaría en false para siempre — el cliente no podría pagar. Un teléfono
   * sin handler es cosmético; un nombre sin handler es un candado.
   */
  showTableName: boolean
  tableName: string
  onChangeTableName: (v: string) => void
  /** Modo mesa (cualquier submodo): campo de teléfono opcional. */
  showQrPhone?: boolean
  qrPhone?: string
  onChangeQrPhone?: (v: string) => void
  /** Qué texto de ayuda va bajo el teléfono: el de mesa o el de "para llevar". */
  qrPhoneHelpKey?: 'tablePhoneHelp' | 'takeoutPhoneHelp'
  /** Label de la mesa; presente ⇒ se dibuja el aviso de "esto va a tu mesa". */
  tableNoticeLabel?: string | null
  // ─── Delivery (Fase 4b) ────────────────────────────────────────────────────
  /**
   * El local tiene delivery propio encendido Y este no es un pedido de mesa por QR.
   * Solo con `true` se dibuja el selector recoger/domicilio. El caller (OrderSheet)
   * ya excluye `tableContext`: un cliente sentado nunca ve la opción.
   */
  deliveryAvailable?: boolean
  deliveryMode?: DeliveryMode
  /**
   * Cambio de recoger ⇄ domicilio. Se llama `onChangeDeliveryMode` y no `onChangeMode`
   * porque ese nombre ya lo ocupa el modo del SchedulePicker (ASAP/agendado).
   * El padre DEBE limpiar dirección + cotización al volver a "pickup".
   */
  onChangeDeliveryMode?: (mode: DeliveryMode) => void
  /** Dirección confirmada en una línea (ya cotizada), o null si todavía no hay. */
  deliveryAddressLine?: string | null
  /** Minutos de preparación del local, para el meta de "Recojo yo". */
  pickupWaitMinutes?: number | null
  /** Tiempo de espera de la tienda (PR K). En vivo: rango, y a domicilio cocina +
   *  repartidor. Fijo o ausente: los textos de siempre. */
  waitEstimate?: WaitEstimate
  /** ETA del courier del quote VIVO. Sin quote no hay número que mostrar. */
  deliveryEtaSeconds?: number | null
  /** Hay cotización congelada. Sin ella NO se puede pagar un pedido a domicilio. */
  hasDeliveryQuote?: boolean
  /** "Cambiar": lleva al paso de dirección. */
  onEditAddress?: () => void
  /** Abre el explicador del envío; solo delivery propio. */
  onWhyFee?: () => void
  // ─── Envío (Fase 1b T11) ────────────────────────────────────────────────────
  /**
   * `shipModeAvailable()` YA evaluado por el padre (depende del carrito — I12:
   * un carrito mixto no califica). Distinto de `deliveryAvailable`: ese es un
   * flag fijo del local, este se recalcula con cada cambio de carrito.
   */
  shippingAvailable?: boolean
  /** Dirección + destinatario confirmados (ya cotizado), o null si todavía no hay. */
  shipAddressLine?: string | null
  /** Hay cotización de envío congelada. Sin ella NO se puede pagar un pedido "ship". */
  hasShipQuote?: boolean
  /** El quote vigente vino con envío gratis (free_over) — la línea de totales dice "Envío gratis" en vez de ocultarse en $0. */
  freeShippingApplied?: boolean
  // ─── Cupón (spec §7.2) ────────────────────────────────────────────────────
  /** GATE-COUPONS del local: false ⇒ no se dibuja NADA del cupón. */
  couponsEnabled?: boolean
  /** Necesario para el preview; en modo mesa es el mismo local del token. */
  locationId?: string
  appliedCoupon?: AppliedCoupon | null
  /** Dólares descontados AHORA (derivado del subtotal vivo por el padre). */
  couponDiscount?: number
  /** El carrito dejó de alcanzar el mínimo: el cupón está en pausa, no aplicado. */
  couponBelowMinimum?: boolean
  couponMissingForMinimum?: number
  /** A1b (TABC): el cupón puesto dejó fuera alguna línea de alcohol. Solo true o ausente. */
  couponExcludedAlcohol?: boolean
  onApplyCoupon?: (c: AppliedCoupon) => void
  /** Quitar el cupón puesto. El total vuelve solo: sale del subtotal vivo. */
  onRemoveCoupon?: () => void
  /** Rechazo que devolvió `/session` al intentar pagar (nadie pagó nada). */
  couponRejection?: (SessionCouponRejection & { code: string }) | null
  onDismissCouponRejection?: () => void
  /** Código de `?coupon=` sin aplicar todavía. */
  pendingCouponCode?: string | null
  onPendingCouponConsumed?: () => void
  // ─── Premio de visitas (PR 5b) ─────────────────────────────────────────────
  /** `features.visit_rewards === true`. false/ausente ⇒ no se dibuja ni se consulta NADA. */
  visitRewardsEnabled?: boolean
  appliedVisitReward?: AppliedVisitReward | null
  /** Monto vista previa del premio aplicado; null = ninguna línea elegible. */
  visitRewardDiscount?: number | null
  visitRewardNotice?: VisitRewardNotice | null
  onApplyVisitReward?: (r: AppliedVisitReward) => void
  onRemoveVisitReward?: () => void
  onDismissVisitRewardNotice?: () => void
  /** Algo del carrito se agotó y el checkout lo quitó, o /session lo rechazó sin que
   *  se pudiera saber qué fue. En los dos casos nadie pagó nada todavía. */
  soldOutNotice?: SoldOutNotice | null
  onDismissSoldOutNotice?: () => void
  /** Súper online (PR 4, D10): nombres de las líneas cuyo precio cambió al abrir el
   *  carrito (el carrito ya trae el nuevo). null/ausente/vacío = sin aviso: todo restaurante. */
  priceChangedNotice?: string[] | null
  onDismissPriceChangedNotice?: () => void
  /** Súper online (Tarea 2b.3): stock EN VIVO de las líneas retail (useRetailStock en
   *  OrderSheet). null/ausente = sin etiquetas: todo restaurante. */
  retailStock?: RetailStockView | null
  // ─── Sustituciones (spec sustituciones §4.2, Task 4 pinta el selector) ────
  /** REQUERIDO a propósito: ningún caller futuro debe olvidar decidir el flag. */
  substitutionsEnabled: boolean
  /** Menú completo del local — de acá sale `substitutionCandidates` por línea. */
  menuItems: MicrositeCartMenuItem[]
  /** El cliente dejó un teléfono usable y no rechazó SMS: gobierna si "avisarme"
   *  es una opción real del selector. */
  contactAllowed: boolean
  /** Aún sin teléfono (se pide en «Tus datos», después del carrito) y sin rechazo de SMS. */
  contactPhonePending?: boolean
  onContinue: () => void
  // ─── Checkout por pasos (spec 2026-10-19 §5, flag checkout_steps_v2) ──────
  /**
   * Qué parte del carrito se pinta. `'all'` (default) es el carrito de siempre,
   * byte a byte (lo fija cartStepMainRegression). En v2 OrderSheet lo monta una
   * vez por paso: `'cart'` artículos + avisos + cupón + totales, `'fulfillment'`
   * solo recoger/domicilio/envío, `'when'` solo el horario.
   */
  variant?: 'all' | 'cart' | 'fulfillment' | 'when'
  /** v2: candado del paso (checkoutV2.gateV2). Reemplaza al candado de 'all'. */
  gate?: GateResult
  /** v2: "A domicilio" deshabilitado (tienda cerrada; no hay programados a domicilio). */
  deliveryDisabled?: boolean
  /** El padre está re-cotizando en silencio: el botón muestra spinner y no avanza. */
  continueBusy?: boolean
  /**
   * Tienda con restaurante y mercado (online_retail_v2, local mixto): sección de cada
   * línea. Con líneas de los dos tipos, el carrito se agrupa en «Restaurante» y
   * «Mercado» (solo presentación: los montos no cambian). Ausente = la lista de
   * siempre, byte a byte (cartStepMainRegression y cartStepCartVariantRegression).
   */
  lineSectionOf?: (line: CartItem) => StorefrontSection
  onClose: () => void
}

// Gris del CTA deshabilitado. Un solo lugar: el botón de pagar y el de aplicar
// cupón tienen que verse igual apagados, y antes cada uno traía su propio hex.
const DISABLED_CTA_BG = '#F3F4F6'

// Total de la línea. Con config: la fórmula única del micrositio (micrositeLineSubtotal:
// base redondeada a tarjeta, modificadores raw; peso como el servidor). Sin config:
// precio guardado + modificadores, como main.
function lineTotalOf(item: CartItem, cfg?: PricingConfig): number {
  if (cfg) return micrositeLineSubtotal(item, cfg)
  const modSum = item.modifiers.reduce((s, m) => s + m.price_delta * (m.quantity ?? 1), 0)
  return (item.price + modSum) * item.quantity
}

export function CartStep({
  cart,
  setCart,
  pricingCfg,
  locale = 'es',
  subtotal,
  taxAmount,
  taxIncluded = false,
  processingFee = 0,
  deliveryFee = 0,
  total,
  accentColor,
  acceptingOrders = true,
  openStatus,
  orderingPaused = false,
  schedulingEnabled = false,
  scheduleDays = [],
  scheduleMode = 'asap',
  scheduledFor = null,
  onChangeMode,
  onChangeSlot,
  showTableName,
  tableName,
  onChangeTableName,
  showQrPhone = false,
  qrPhone = '',
  onChangeQrPhone,
  qrPhoneHelpKey = 'takeoutPhoneHelp',
  tableNoticeLabel = null,
  deliveryAvailable = false,
  deliveryMode = 'pickup',
  onChangeDeliveryMode,
  deliveryAddressLine = null,
  pickupWaitMinutes = null,
  waitEstimate,
  deliveryEtaSeconds = null,
  hasDeliveryQuote = false,
  onEditAddress,
  onWhyFee,
  shippingAvailable = false,
  shipAddressLine = null,
  hasShipQuote = false,
  freeShippingApplied = false,
  couponsEnabled = false,
  locationId,
  appliedCoupon = null,
  couponDiscount = 0,
  couponBelowMinimum = false,
  couponMissingForMinimum = 0,
  couponExcludedAlcohol = false,
  onApplyCoupon,
  onRemoveCoupon,
  couponRejection = null,
  onDismissCouponRejection,
  pendingCouponCode = null,
  onPendingCouponConsumed,
  visitRewardsEnabled = false,
  appliedVisitReward = null,
  visitRewardDiscount = null,
  visitRewardNotice = null,
  onApplyVisitReward,
  onRemoveVisitReward,
  onDismissVisitRewardNotice,
  soldOutNotice = null,
  onDismissSoldOutNotice,
  priceChangedNotice = null,
  onDismissPriceChangedNotice,
  retailStock = null,
  substitutionsEnabled,
  menuItems,
  contactAllowed,
  contactPhonePending = false,
  onContinue,
  continueBusy = false,
  onClose,
  variant = 'all',
  gate,
  deliveryDisabled = false,
  lineSectionOf,
}: Props) {
  const t = useTranslations('public.microsite')
  const tOrder = useTranslations('public.onlineOrder')
  const tCommon = useTranslations('common')
  const t2 = useTranslations('public.onlineOrder.checkout')
  const liveWait = waitEstimate?.mode === 'live' ? waitEstimate : null
  const tc = useTranslations('public.onlineOrder.checkout.coupon')

  // Botón de pánico: con la pausa activa NO se puede pagar en ningún modo (cierre
  // general). Tiene prioridad sobre el aviso de "cerrado por horario".
  const paused = orderingPaused === true
  // Delivery elegido pero sin cotización congelada: NO se puede avanzar. Sin quote
  // el pago no tendría fee de envío que cobrar (o cobraría uno inventado), así que
  // el cliente tiene que pasar por el paso de dirección primero.
  const isDelivery = deliveryAvailable && deliveryMode === 'delivery'
  const needsAddress = isDelivery && !hasDeliveryQuote
  // Envío (Fase 1b T11): mismo criterio que delivery, cotización propia.
  const isShip = shippingAvailable && deliveryMode === 'ship'
  const needsShipAddress = isShip && !hasShipQuote
  // Se puede ir a pago si: modo agendado con slot elegido, o modo ASAP con el
  // local aceptando pedidos ahora — y, en domicilio/envío, con la dirección ya
  // cotizada. El nombre es obligatorio en modo mesa: sin él, el mesero no tiene
  // a nombre de quién entregar cuando el cliente viene al mostrador — que es
  // justo lo que pasa cuando se movió de mesa.
  const nameOk = nameGateSatisfied(showTableName, tableName)
  // Solo DISPLAY: el campo nace vacío, así que sin esto el cliente de mesa era
  // recibido con un error rojo antes de tocar nada — y la ayuda que explica POR
  // QUÉ pedimos el nombre solo aparecía cuando ya lo había escrito, o sea nunca
  // para el que la necesitaba. El candado sigue siendo `nameOk` en `canCheckout`.
  const [nameTouched, setNameTouched] = useState(false)
  const showNameError = nameTouched && !nameOk
  // El teléfono es OPCIONAL, pero uno escrito a medias es un bug de dinero: el
  // POST ocurre después del cobro y el servidor lo rechaza con 400 → cobrado sin
  // orden. En blanco sigue pasando; ver phoneGateSatisfied.
  const phoneOk = phoneGateSatisfied(showQrPhone, qrPhone)
  const canCheckout = paused
    ? false
    : needsAddress || needsShipAddress
      ? false
      : !nameOk
        ? false
        : !phoneOk
          ? false
          : scheduleMode === 'scheduled' ? !!scheduledFor : acceptingOrders
  const showClosedNotice = !paused && scheduleMode === 'asap' && !acceptingOrders

  // Checkout por pasos: qué bloques pinta esta instancia. En 'all' todo, como siempre.
  const v2 = variant !== 'all'
  const showItems = variant === 'all' || variant === 'cart'
  const showFulfillment = variant === 'all' || variant === 'fulfillment'
  const showMoney = variant === 'all' || variant === 'cart'

  function updateQuantity(cartId: string, delta: number) {
    setCart(
      cart
        .map((item) =>
          item.cartId === cartId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    )
  }

  function removeItem(cartId: string) {
    setCart(cart.filter((item) => item.cartId !== cartId))
  }

  const isEmpty = cart.length === 0
  const ctaEnabled = gate ? gate.ok : !isEmpty && canCheckout

  // Sustituciones (spec sustituciones §4.2): qué línea tiene la hoja "Si no
  // hay <ítem>" abierta, o null si ninguna. Un solo estado — sólo una hoja a
  // la vez, igual que el resto de las hojas del micrositio.
  const [lineSheetFor, setLineSheetFor] = useState<string | null>(null)
  const lineSheetItem = lineSheetFor ? cart.find((c) => c.cartId === lineSheetFor) ?? null : null

  // ─── Cupón ─────────────────────────────────────────────────────────────────
  // 🔴 Todo lo de acá es VISTA PREVIA. El monto que se cobra lo decide
  // /api/online-orders/session recomputando desde precios de la BD; este bloque
  // solo existe para que el cliente vea el descuento ANTES de comprometerse.
  // Valor inicial del campo: si la visita llegó por el enlace o el QR del panel
  // de cupones, el código YA está escrito en el primer render. Inicializador de
  // useState y no un efecto a propósito — así el deep link deja rastro en el
  // markup y se puede probar sin DOM.
  const [couponInput, setCouponInput] = useState(() => deepLinkInitialInput(pendingCouponCode))
  const [couponBusy, setCouponBusy] = useState(false)
  const [couponError, setCouponError] = useState<CouponReasonKey | null>(null)
  const [couponErrorMin, setCouponErrorMin] = useState<number | null>(null)
  // A1b: base SIN alcohol contra la que el servidor midió ese mínimo (solo si excluyó algo).
  const [couponErrorMeasured, setCouponErrorMeasured] = useState<number | null>(null)
  const couponAliveRef = useRef(true)
  useEffect(() => {
    couponAliveRef.current = true
    return () => {
      couponAliveRef.current = false
    }
  }, [])
  const showCoupon = couponsEnabled && !!locationId && !isEmpty
  // v2: el cupón arranca como un enlace discreto (spec 2026-10-19 §5.5) y se abre
  // en línea. Abierto de entrada si ya hay algo que mostrar: código del deep
  // link, cupón puesto o un rechazo que explicar.
  const [couponOpen, setCouponOpen] = useState(
    () => variant === 'all' || !!pendingCouponCode || !!appliedCoupon || !!couponRejection,
  )
  const couponExpanded = couponOpen || !!appliedCoupon || !!couponRejection || !!couponError

  // Un solo camino para el código tecleado y para el del deep link: la decisión
  // (y la matemática) viven en runCouponApply, fuera del componente.
  async function applyCoupon(raw: string) {
    if (!locationId || couponBusy) return
    setCouponBusy(true)
    setCouponError(null)
    setCouponErrorMin(null)
    setCouponErrorMeasured(null)
    // Aplicar algo nuevo cierra el aviso del rechazo anterior: describía un
    // cupón que ya no está puesto.
    onDismissCouponRejection?.()
    // A1b: las líneas de ese subtotal, para que el preview mida la base sin alcohol.
    const lines = pricingCfg ? micrositeCouponRequestLines(cart, pricingCfg) : undefined
    const out = await runCouponApply({ locationId, code: raw, subtotal, lines })
    if (!couponAliveRef.current) return
    setCouponBusy(false)
    if (out.status === 'rejected') {
      setCouponError(out.reasonKey)
      setCouponErrorMin(out.minSubtotal)
      setCouponErrorMeasured(out.measuredSubtotal ?? null)
      return
    }
    onApplyCoupon?.(out.coupon)
    setCouponInput('')
  }

  // Deep link `?coupon=CODIGO`: el enlace y el QR que el panel ya reparte. El
  // QUÉ hacer lo decide `deepLinkDecision` (probado aparte); acá solo queda el
  // disparo, porque validar contra el servidor exige un efecto.
  useEffect(() => {
    const decision = deepLinkDecision({
      pendingCouponCode,
      couponsEnabled,
      locationId,
      appliedCoupon,
      cartIsEmpty: isEmpty,
    })
    if (decision === 'none' || decision === 'wait') return
    if (decision === 'consume') {
      onPendingCouponConsumed?.()
      return
    }
    onPendingCouponConsumed?.()
    void applyCoupon(pendingCouponCode as string)
    // `applyCoupon` y los callbacks se re-crean en cada render; incluirlos
    // dispararía el efecto en bucle. Las deps reales son las de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCouponCode, couponsEnabled, locationId, appliedCoupon, isEmpty])

  // El orden importa: pausa y dirección son bloqueos más duros, y el
  // "cerrado" es el catch-all. Sin la rama de nombre, el carrito de
  // mesa arrancaba diciendo "Cerrado" con el local ABIERTO — el campo
  // nace vacío, así que ese era el estado por defecto de la pantalla.
  const continueLabel = gate
    ? gate.ok || gate.reason === 'empty'
      ? `${tCommon('next')} →`
      : gate.reason === 'paused'
        ? tOrder('pausedButton')
        : t('openStatus.closed')
    : (
    canCheckout || isEmpty
      ? `${tCommon('next')} →`
      : paused
        ? tOrder('pausedButton')
        : needsAddress
          ? t2('addressCtaNeedAddress')
          : !nameOk
            ? t2('tableNameMissing')
            : !phoneOk
              ? t2('qrPhoneInvalid')
              : t('openStatus.closed')
  )

  // Grupos «Restaurante» / «Mercado»: solo con `lineSectionOf` y los dos tipos en el
  // carrito. Sin la prop (todo restaurante) no se calcula nada.
  const lineGroups = lineSectionOf ? groupCartLines(cart, lineSectionOf) : null
  const groups = lineGroups && lineGroups.length > 1 ? lineGroups : null

  // Una línea del carrito. La usan la lista de siempre y los grupos «Restaurante» /
  // «Mercado»: mismo markup en los dos caminos.
  function renderLine(item: CartItem) {
    const lineTotal = lineTotalOf(item, pricingCfg)
    // Stock vivo (Tarea 2b.3): solo con `retailStock`, que un restaurante no tiene.
    const stock = retailStock ? retailStockBadge(retailStock, item) : null
    return (
      <li
        key={item.cartId}
        className={`rounded-2xl border border-gray-100 bg-white ${v2 ? 'p-3' : 'p-4'} shadow-sm`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Súper online: «Leche · Galón» / «Banana · 1,5 lb». Un plato: item.name tal cual. */}
            <p className="font-semibold text-gray-900 truncate">{micrositeLineName(item, locale)}</p>
            {stock && (
              <p className={`text-xs font-semibold mt-0.5 ${stock.kind === 'sold_out' ? 'text-red-600 uppercase tracking-wide' : 'text-gray-500'}`}>
                {stock.kind === 'sold_out'
                  ? t('soldOut')
                  : item.weightMilli != null
                    ? t('onlyLeftWeight', { amount: stock.left, weight: micrositeWeightLabel(stock.left * 1000, item.weightUnit, locale) })
                    : t('onlyLeft', { count: stock.left })}
              </p>
            )}
            {item.modifiers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {item.modifiers.map((m) => formatModifierLabel(m.option_name, m.quantity)).join(', ')}
              </p>
            )}
            {item.specialInstructions && (
              <p className="text-xs text-gray-500 italic mt-1">
                “{item.specialInstructions}”
              </p>
            )}
            {substitutionsEnabled && (
              <button
                type="button"
                onClick={() => setLineSheetFor(item.cartId)}
                className="mt-1 text-xs font-semibold"
                style={{ color: accentColor }}
              >
                {item.substitutionItemId
                  ? t2('substitution.chosen', {
                      name: menuItems.find((m) => m.id === item.substitutionItemId)?.name ?? '',
                    })
                  : item.substitutionPref
                    ? t2(`substitution.${item.substitutionPref}`)
                    : t2('substitution.lineLink')}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeItem(item.cartId)}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 hover:bg-red-50 hover:text-red-600 text-gray-500 flex items-center justify-center"
            aria-label="Quitar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className={`${v2 ? 'mt-2' : 'mt-3'} flex items-center justify-between`}>
          {/* Una línea por peso va con cantidad 1 (el peso ya está en el nombre):
              sin «−/+», que la mandarían como 2 × peso y el servidor la rechazaría. */}
          {item.weightMilli != null ? <div /> : (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => updateQuantity(item.cartId, -1)}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              aria-label="Disminuir"
            >
              <Minus className="w-4 h-4 text-gray-700" />
            </button>
            <span className="w-6 text-center font-semibold text-gray-900">
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={() => updateQuantity(item.cartId, +1)}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              aria-label="Aumentar"
            >
              <Plus className="w-4 h-4 text-gray-700" />
            </button>
          </div>
          )}
          <p className="font-bold text-gray-900">${lineTotal.toFixed(2)}</p>
        </div>
      </li>
    )
  }

  return (
    <>
    <div className="flex flex-col h-full">
      {/* Header (en v2 lo pone OrderSheet: "Paso X de Y" + total) */}
      {!v2 && (
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-900">{tOrder('yourOrder')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          aria-label={tCommon('close')}
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>
      </div>
      )}

      {/* Scroll area */}
      <div className={`flex-1 min-h-0 overflow-y-auto ${v2 ? 'px-4 py-3' : 'px-5 py-4'}`}>
        {!showItems ? null : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-base text-gray-600">{tCommon('noResults')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 rounded-full px-6 py-3 text-white text-sm font-semibold transition hover:opacity-95 active:scale-[0.99]"
              style={{ backgroundColor: accentColor }}
            >
              {t('viewMenu')}
            </button>
          </div>
        ) : (
          groups ? (
            <div className={v2 ? 'space-y-4' : 'space-y-5'}>
              {groups.map((g) => (
                <section key={g.section} data-testid={`cart-group-${g.section}`}>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    {g.section === 'market' ? t('grocery.sections.tabMarket') : t('grocery.sections.tabRestaurant')}
                  </h3>
                  <p className="mt-0.5 mb-2.5 text-xs text-gray-500">
                    {g.section === 'market'
                      ? g.lines.some((l) => l.weightMilli != null)
                        ? `${t('grocery.sections.marketLine')} ${t('grocery.sections.marketWeightLine')}`
                        : t('grocery.sections.marketLine')
                      : t('grocery.sections.restaurantLine')}
                  </p>
                  <ul className={v2 ? 'space-y-2.5' : 'space-y-4'}>{g.lines.map(renderLine)}</ul>
                </section>
              ))}
            </div>
          ) : (
          <ul className={v2 ? 'space-y-2.5' : 'space-y-4'}>
            {cart.map(renderLine)}
          </ul>
          )
        )}
        {showFulfillment && !v2 && !isEmpty && (deliveryAvailable || shippingAvailable) && (
          <p className="mt-5 mb-2.5 text-xs font-bold uppercase tracking-wider text-gray-400">{t2('fulfillmentTitle')}</p>
        )}
        {/* Recoger ⇄ a domicilio ⇄ envío. Tarjetas, no un pill chiquito centrado:
            es la decisión más importante del carrito y necesita peso propio,
            ícono dibujado (nunca emoji) y el dato que de verdad decide — cuánto
            tarda. El precio del envío NO se muestra acá: aparece recién en el
            total. */}
        {showFulfillment && (deliveryAvailable || shippingAvailable) && !isEmpty && (
          <div className="mb-3 space-y-2.5">
            {(
              [
                {
                  key: 'pickup' as const,
                  Icon: Store,
                  label: t2('modePickup'),
                  meta:
                    typeof pickupWaitMinutes === 'number'
                      ? liveWait
                        ? waitText(liveWait, 'pickupMeta', t2)
                        : t2('modePickupMeta', { minutes: pickupWaitMinutes })
                      : null,
                  trailing: t2('modePickupFree'),
                },
                ...(deliveryAvailable
                  ? [
                      {
                        key: 'delivery' as const,
                        Icon: Bike,
                        label: t2('modeDelivery'),
                        // Sin cotización todavía no hay ETA real: se dice qué pasa, no un
                        // número inventado. Con quote vivo sí se muestra el de Uber.
                        meta: deliveryDisabled
                          ? t2('deliveryWhenOpen')
                          : typeof deliveryEtaSeconds === 'number'
                            ? liveWait
                              ? waitText(withCourier(liveWait, Math.round(deliveryEtaSeconds / 60)), 'deliveryMeta', t2)
                              : t2('modeDeliveryEta', { minutes: Math.round(deliveryEtaSeconds / 60) })
                            : t2('modeDeliveryGeneric'),
                        trailing: null,
                      },
                    ]
                  : []),
                // Envío (Fase 1b T11): `shippingAvailable` YA exige que TODO el
                // carrito sea shippable (I12) — si aparece, un carrito mixto
                // desaparece la opción sola, no hace falta filtrarla acá.
                ...(shippingAvailable
                  ? [
                      {
                        key: 'ship' as const,
                        Icon: Package,
                        label: t2('modeShip'),
                        meta: t2('modeShipGeneric'),
                        trailing: null,
                      },
                    ]
                  : []),
              ]
            ).map((opt) => {
              const selected = deliveryMode === opt.key
              const off = deliveryDisabled && opt.key === 'delivery'
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onChangeDeliveryMode?.(opt.key)}
                  aria-pressed={selected}
                  {...(off ? { disabled: true } : {})}
                  className={`w-full flex items-center gap-3 rounded-2xl border-[1.5px] ${v2 ? 'p-3' : 'p-3.5'} text-left transition${off ? ' opacity-50 cursor-not-allowed' : ''}`}
                  style={{
                    borderColor: selected ? accentColor : '#E5E7EB',
                    backgroundColor: selected ? `${accentColor}0D` : '#FFFFFF',
                  }}
                >
                  <span
                    className="w-[38px] h-[38px] flex-shrink-0 rounded-[11px] flex items-center justify-center"
                    style={{ backgroundColor: selected ? '#FFFFFF' : '#F9FAFB' }}
                  >
                    <opt.Icon
                      className="w-[19px] h-[19px]"
                      style={{ color: selected ? accentColor : '#6B7280' }}
                    />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-gray-900">{opt.label}</span>
                    {opt.meta && (
                      <span className="block text-xs text-gray-500 mt-0.5">{opt.meta}</span>
                    )}
                  </span>
                  {opt.trailing && (
                    <span className="flex-shrink-0 text-xs font-semibold text-gray-500">
                      {opt.trailing}
                    </span>
                  )}
                  <span
                    className="w-[19px] h-[19px] flex-shrink-0 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: selected ? accentColor : 'transparent',
                      border: selected ? 'none' : '1.5px solid #D1D5DB',
                    }}
                  >
                    {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                </button>
              )
            })}
          </div>
        )}
        {/* Dirección: confirmada (compacta + "Cambiar") o pendiente. La versión
            pendiente NO es decorativa — el CTA principal está deshabilitado sin
            cotización, así que esta fila es el camino vivo al paso de dirección
            (si no, el carrito sería un callejón sin salida en modo domicilio/envío).
            Misma fila para delivery y ship — el destino (onEditAddress) es el
            mismo paso "address" en los dos modos. */}
        {showFulfillment && (isDelivery || isShip) && !isEmpty && (
          <button
            type="button"
            onClick={onEditAddress}
            className="mb-3 w-full flex items-center gap-2.5 rounded-xl bg-gray-50 px-3.5 py-3 text-left transition hover:bg-gray-100"
            aria-label={(isDelivery ? deliveryAddressLine : shipAddressLine) ? t2('addressChangeShort') : t2('addressAddShort')}
          >
            <MapPin className="w-[17px] h-[17px] flex-shrink-0" style={{ color: accentColor }} />
            <span
              className={`flex-1 min-w-0 truncate text-[13px] ${
                (isDelivery ? deliveryAddressLine : shipAddressLine) ? 'text-gray-700' : 'text-gray-500'
              }`}
            >
              {(isDelivery ? deliveryAddressLine : shipAddressLine) ?? t2('addressCtaNeedAddress')}
            </span>
            {/* Chevron en vez de un "Agregar" en color de marca: ese texto competía
                con el CTA principal por la misma atención. */}
            <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400" />
          </button>
        )}
        {/* v2 · "¿Cuándo?": el horario es el contenido del paso, no un extra del pie. */}
        {variant === 'when' && !isEmpty && (
          <div className="space-y-3">
            {!paused && !acceptingOrders && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-semibold text-amber-900">{t2('closedCanSchedule')}</p>
              </div>
            )}
            {schedulingEnabled && !isDelivery && scheduleDays.length > 0 ? (
              <SchedulePicker
                days={scheduleDays}
                mode={scheduleMode}
                scheduledFor={scheduledFor}
                asapAvailable={acceptingOrders}
                accentColor={accentColor}
                onChangeMode={onChangeMode ?? (() => {})}
                onChangeSlot={onChangeSlot ?? (() => {})}
              />
            ) : (
              typeof pickupWaitMinutes === 'number' && !isDelivery && (
                <p className="text-sm text-gray-600">
                  {liveWait ? waitText(liveWait, 'pickupMeta', t2) : t2('modePickupMeta', { minutes: pickupWaitMinutes })}
                </p>
              )
            )}
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div
        className={`border-t border-gray-100 bg-white ${v2 ? 'px-4 pt-3' : 'px-5 pt-4'}`}
        style={{ paddingBottom: v2 ? 'max(0.75rem, env(safe-area-inset-bottom))' : 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {/* Lo que se agotó mientras el cliente armaba el pedido. Va PRIMERO en el pie
            y aunque el carrito haya quedado vacío: es la explicación de por qué
            cambió. Nadie pagó nada, el checkout lo detectó antes de cobrar. */}
        {showItems && soldOutNotice && (
          <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" role="status">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {soldOutNotice.kind === 'removed' ? t2('soldOutRemovedTitle') : t2('soldOutChangedTitle')}
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {soldOutNotice.kind === 'removed'
                    ? t2('soldOutRemovedBody', { items: soldOutNotice.names.join(', ') })
                    : soldOutNotice.kind === 'message'
                      ? soldOutNotice.text
                      : t2('soldOutChangedBody')}
                </p>
              </div>
              {onDismissSoldOutNotice && (
                <button
                  type="button"
                  onClick={onDismissSoldOutNotice}
                  aria-label={t2('soldOutDismiss')}
                  className="flex-shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full text-amber-900 hover:bg-amber-100 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
        {/* Súper online (D10): el precio de algo retail cambió desde que se agregó. El
            carrito ya muestra el de hoy; esto dice de qué, antes de pagar. */}
        {showItems && priceChangedNotice && priceChangedNotice.length > 0 && (
          <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" role="status">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">{t('grocery.priceChangedTitle')}</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {t('grocery.priceChangedBody', { items: priceChangedNotice.join(', ') })}
                </p>
              </div>
              {onDismissPriceChangedNotice && (
                <button
                  type="button"
                  onClick={onDismissPriceChangedNotice}
                  aria-label={t2('soldOutDismiss')}
                  className="flex-shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full text-amber-900 hover:bg-amber-100 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
        {/* Sin pedidos PROGRAMADOS a domicilio por ahora (decisión 2026-09-05): el
            despacho a la hora del slot (autoDispatch, guardia de 20 min) tiene que
            estar en producción y probado antes de ofrecerlo. Pickup sigue igual. */}
        {variant === 'all' && schedulingEnabled && !isEmpty && !isDelivery && (
          <div className="mb-3">
            <SchedulePicker
              days={scheduleDays}
              mode={scheduleMode}
              scheduledFor={scheduledFor}
              asapAvailable={acceptingOrders}
              accentColor={accentColor}
              onChangeMode={onChangeMode ?? (() => {})}
              onChangeSlot={onChangeSlot ?? (() => {})}
            />
          </div>
        )}
        {(variant === 'all' || variant === 'cart') && paused && !isEmpty && (
          <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">{tOrder('pausedTitle')}</p>
            <p className="text-xs text-amber-800 mt-0.5">{tOrder('pausedBody')}</p>
          </div>
        )}
        {(variant === 'all' || (variant === 'cart' && !(schedulingEnabled && scheduleDays.length > 0))) && showClosedNotice && !isEmpty && (
          <div className="mb-3">
            <ClosedNotice status={openStatus ?? null} />
          </div>
        )}
        {showItems && tableNoticeLabel && !isEmpty && (
          /* Aviso de mesa: el cliente eligió "En la mesa" y su comida va a ir
             físicamente ahí. Sin esto, el que se levanta no se entera de que el
             mesero lo va a buscar en la mesa del sticker. Mismo patrón visual
             que los avisos de pausa/cierre de arriba. */
          <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">
              {t2('tableNotice', { table: tableNoticeLabel })}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">{t2('tableNoticeMove')}</p>
          </div>
        )}
        {showItems && showTableName && !isEmpty && (
          <div className="mb-3">
            <label
              htmlFor="qr-table-name"
              className="block text-sm font-semibold text-gray-700 mb-1.5"
            >
              {t2('tableNameLabel')}
            </label>
            {/* `aria-describedby` apunta al <p> de abajo: sin ese enlace el lector
                de pantalla anuncia "inválido" sin decir por qué, y el gris→rojo
                sería información SOLO por color.
                `aria-invalid` se enciende al salir del campo, no al abrir la
                pantalla: marcar inválido algo que el cliente todavía no tocó es
                gritarle un error que él no cometió. */}
            <input
              id="qr-table-name"
              type="text"
              value={tableName}
              onChange={(e) => onChangeTableName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              placeholder={t2('tableNamePlaceholder')}
              autoComplete="name"
              maxLength={100}
              aria-invalid={showNameError || undefined}
              aria-describedby="qr-table-name-help"
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
            <p
              id="qr-table-name-help"
              className={`mt-1 text-xs ${showNameError ? 'text-red-600' : 'text-gray-500'}`}
            >
              {showNameError ? t2('tableNameMissing') : t2('tableNameHelp')}
            </p>
          </div>
        )}
        {showItems && showQrPhone && !isEmpty && (
          <div className="mb-3">
            <label
              htmlFor="qr-phone"
              className="block text-sm font-semibold text-gray-700 mb-1.5"
            >
              {t2('qrPhoneLabel')}
            </label>
            {/* Mismo trato que el nombre de arriba: `aria-invalid` + el <p> ligado
                por `aria-describedby`, para que el gris→rojo no sea información
                SOLO por color y el lector de pantalla diga POR QUÉ está inválido. */}
            <PhoneInput
              id="qr-phone"
              value={qrPhone}
              onChange={(v) => onChangeQrPhone?.(v)}
              placeholder="(555) 000-0000"
              aria-invalid={!phoneOk}
              aria-describedby="qr-phone-help"
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
            <p
              id="qr-phone-help"
              className={`mt-1 text-xs ${phoneOk ? 'text-gray-500' : 'text-red-600'}`}
            >
              {phoneOk ? t2(qrPhoneHelpKey) : t2('qrPhoneInvalid')}
            </p>
          </div>
        )}
        {/* ─── Cupón ───────────────────────────────────────────────────────
            Va JUNTO a los totales, antes del botón de pagar: el cliente ve el
            descuento antes de comprometerse, no después. */}
        {/* Premio de visitas (PR 5b): junto al cupón, antes de los totales. Un pedido
            lleva un solo cupón: el padre quita uno al aplicar el otro. Sin el flag
            no monta nada ni hace ninguna petición. */}
        {showMoney && !isEmpty && !!locationId && (
          <VisitRewardWebOffer
            enabled={visitRewardsEnabled}
            locationId={locationId}
            accentColor={accentColor}
            applied={appliedVisitReward}
            discount={visitRewardDiscount}
            notice={visitRewardNotice}
            onApply={(r) => onApplyVisitReward?.(r)}
            onRemove={() => onRemoveVisitReward?.()}
            onDismissNotice={() => onDismissVisitRewardNotice?.()}
          />
        )}
        {showMoney && showCoupon && !couponExpanded && (
          <button
            type="button"
            onClick={() => setCouponOpen(true)}
            className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: accentColor }}
          >
            <Tag className="w-4 h-4" />
            {t2('couponLink')}
          </button>
        )}
        {showMoney && showCoupon && couponExpanded && (
          <div className="mb-3">
            {/* Rechazo de /session (P1-18): el cupón murió entre el carrito y el
                pago. NADIE PAGÓ NADA — /session rechaza antes de crear la sesión
                de pago. El total de abajo ya está corregido; esto explica por qué
                cambió y deja seguir comprando sin el cupón. */}
            {couponRejection && (
              <div
                className="mb-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
                role="status"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      {tc('rejectedTitle', { code: couponRejection.code })}
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      {couponRejection.reasonKey === 'minSubtotal' && couponRejection.minSubtotal != null
                        ? tc('rejectedMinSubtotal', { min: `$${couponRejection.minSubtotal.toFixed(2)}` })
                        : tc(`reason.${couponRejection.reasonKey}`)}
                    </p>
                    <p className="text-xs text-amber-800 mt-1.5 font-semibold">
                      {tc('rejectedTotalWithout', { total: `$${total.toFixed(2)}` })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDismissCouponRejection?.()}
                    aria-label={tc('rejectedDismiss')}
                    className="flex-shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full text-amber-900 hover:bg-amber-100 flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {appliedCoupon ? (
              <div className="rounded-xl border border-gray-200 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
                  <span className="text-sm font-bold text-gray-900 tracking-wide">
                    {appliedCoupon.code}
                  </span>
                  <span className="flex-1" />
                  {!couponBelowMinimum && (
                    <span className="text-sm font-bold" style={{ color: accentColor }}>
                      −${couponDiscount.toFixed(2)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setCouponError(null)
                      setCouponErrorMin(null)
                      setCouponErrorMeasured(null)
                      onDismissCouponRejection?.()
                      onRemoveCoupon?.()
                    }}
                    aria-label={tc('removeAria', { code: appliedCoupon.code })}
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 hover:bg-red-50 hover:text-red-600 text-gray-500 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {/* El carrito bajó del mínimo: el cupón NO se tira (el cliente
                    puede volver a subir el carrito), pero deja de descontar y el
                    total de abajo ya no lo incluye. Se dice cuánto falta. */}
                {couponBelowMinimum && (
                  <p className="mt-1.5 text-xs text-amber-800">
                    {tc('pausedBelowMinimum', { missing: `$${couponMissingForMinimum.toFixed(2)}` })}
                  </p>
                )}
                {/* A1b (TABC): el cupón no rebaja el alcohol. Solo con la bandera;
                    sin ella el markup es el de antes. */}
                {couponExcludedAlcohol && (
                  <p className="mt-1.5 text-xs text-gray-500" data-testid="coupon-excludes-alcohol">
                    {tc('excludesAlcohol')}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label htmlFor="microsite-coupon" className="sr-only">
                  {tc('label')}
                </label>
                <div className="flex gap-2">
                  <input
                    id="microsite-coupon"
                    type="text"
                    value={couponInput}
                    onChange={(e) => {
                      setCouponInput(e.target.value.toUpperCase())
                      setCouponError(null)
                      setCouponErrorMin(null)
                      setCouponErrorMeasured(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (couponInput.trim()) void applyCoupon(couponInput)
                      }
                    }}
                    placeholder={tc('placeholder')}
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={20}
                    aria-invalid={!!couponError || undefined}
                    aria-describedby="microsite-coupon-help"
                    className="flex-1 min-w-0 rounded-xl border border-gray-200 px-3 py-3 text-base tracking-wide uppercase focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                  />
                  <button
                    type="button"
                    onClick={() => void applyCoupon(couponInput)}
                    disabled={couponBusy || !couponInput.trim()}
                    className={`flex-shrink-0 rounded-xl px-4 py-3 text-sm font-bold transition active:scale-[0.99] ${
                      couponBusy || !couponInput.trim()
                        ? 'cursor-not-allowed text-gray-400'
                        : 'text-white hover:opacity-95'
                    }`}
                    // Mismo criterio que el CTA de abajo: deshabilitado es gris
                    // sólido, no el color de marca desvaído (que se lee como roto).
                    style={{
                      backgroundColor: couponBusy || !couponInput.trim() ? DISABLED_CTA_BG : accentColor,
                    }}
                  >
                    {couponBusy ? tc('applying') : tc('apply')}
                  </button>
                </div>
                {/* El motivo del rechazo, SIEMPRE el específico que devolvió el
                    servidor: "ya venció" y "no llega al mínimo" mandan al cliente
                    a hacer cosas distintas, y un "código inválido" genérico lo
                    deja corrigiendo un código que estaba bien escrito. */}
                <p
                  id="microsite-coupon-help"
                  className={`mt-1 text-xs ${couponError ? 'text-red-600' : 'text-gray-500'}`}
                >
                  {couponError
                    ? couponError === 'minSubtotal' && couponErrorMin != null
                      ? tc('reasonMinSubtotalMissing', {
                          min: `$${couponErrorMin.toFixed(2)}`,
                          // A1b: sobre la base SIN alcohol que midió el servidor;
                          // sin alcohol es `mínimo − subtotal`, como siempre.
                          missing: `$${Math.max(0, couponShortfall(couponErrorMin, subtotal, couponErrorMeasured)).toFixed(2)}`,
                        })
                      : tc(`reason.${couponError}`)
                    : tc('help')}
                </p>
              </div>
            )}
          </div>
        )}
        {showMoney && (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>{tCommon('subtotal')}</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {/* El descuento baja el subtotal ANTES del impuesto — el `taxAmount`
              que llega ya viene calculado sobre el subtotal descontado (mismo
              orden que lib/online-orders/pricing en el servidor). */}
          {couponDiscount > 0 && appliedCoupon && (
            <div className="flex justify-between font-semibold" style={{ color: accentColor }}>
              <span>{tc('lineLabel', { code: appliedCoupon.code })}</span>
              <span>−${couponDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-600">
            <span>{tCommon(taxIncluded ? 'taxIncluded' : 'tax')}</span>
            <span>${taxAmount.toFixed(2)}</span>
          </div>
          {processingFee > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>{tCommon('processingFee')}</span>
              <span>${processingFee.toFixed(2)}</span>
            </div>
          )}
          {/* Envío — DESPUÉS del impuesto, nunca dentro del subtotal (el servidor
              suma deliveryFeeCents/shippingFeeCents al total ya gravado). En envío
              gratis (`free_over`) el fee llega en $0: SIN el `|| (... && deliveryFee === 0)`
              la línea desaparecería del todo y "Envío gratis" jamás se mostraría.
              El guard `deliveryFee === 0` (ronda de fix 1, MINOR-b) es defensivo:
              si `freeShippingApplied` viniera en true con un fee > 0 (no debería
              pasar nunca, pero es dinero), se muestra el monto REAL, nunca "gratis"
              sobre un cargo que no lo es. */}
          {(deliveryFee > 0 || (isShip && freeShippingApplied && deliveryFee === 0)) && (
            <div className="flex justify-between text-gray-600">
              <span>
                {isShip ? t2('shippingFeeLabel') : t2('deliveryFeeLabel')}
                {!isShip && onWhyFee && deliveryFee > 0 && (
                  <>
                    {' '}
                    <WhyFeeLink onClick={onWhyFee} className="text-gray-500" />
                  </>
                )}
              </span>
              <span>
                {isShip && freeShippingApplied && deliveryFee === 0
                  ? t2('shippingFeeFree')
                  : `$${deliveryFee.toFixed(2)}`}
              </span>
            </div>
          )}
          <div className="flex justify-between font-bold text-gray-900 text-base pt-1">
            <span>{tCommon('total')}</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
        )}
        <button
          type="button"
          onClick={onContinue}
          disabled={!ctaEnabled || continueBusy}
          className={`${v2 ? (showMoney ? 'mt-3 ' : '') + 'w-full rounded-full py-3' : 'mt-4 w-full rounded-full py-4'} text-base font-bold transition active:scale-[0.99] ${
            !ctaEnabled
              ? 'cursor-not-allowed text-gray-400'
              : 'text-white hover:opacity-95'
          }`}
          // Deshabilitado = gris sólido, no el color de marca al 40%: en rosa pálido
          // el botón se leía como roto en vez de como "te falta un paso".
          style={{ backgroundColor: !ctaEnabled ? DISABLED_CTA_BG : accentColor }}
        >
          {continueBusy ? (
            <span className="inline-flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> {t2('addressChecking')}
            </span>
          ) : (
            continueLabel
          )}
        </button>
        {!v2 && !isEmpty && (
          <div className="mt-3 flex justify-center">
            <AcceptedCards tone="light" label={null} />
          </div>
        )}
      </div>
    </div>
    {lineSheetItem && (
      <SubstitutionLineSheet
        open
        onClose={() => setLineSheetFor(null)}
        line={lineSheetItem}
        candidates={cartLineSubstitutionCandidates(lineSheetItem, menuItems)}
        contactAllowed={contactAllowed}
        contactPhonePending={contactPhonePending}
        accentColor={accentColor}
        pricingCfg={pricingCfg}
        onSave={(pref, itemId) =>
          setCart(
            cart.map((c) =>
              c.cartId === lineSheetItem.cartId ? { ...c, substitutionPref: pref, substitutionItemId: itemId } : c,
            ),
          )
        }
      />
    )}
    </>
  )
}
